import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useCanAccess } from "ra-core";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  referralRequest,
  type ReferralDiagnostics,
  type CampaignPreview,
} from "../providers/aws/referralClient";

// A reward notice that ended in any of these states will not arrive on its
// own: the customer has the credits and has not been told. `unknown` is the
// worst of them, because the contact is also fenced against further sends
// until an operator resolves it.
const NOTICE_FAILED = new Set([
  "unknown",
  "terminal_failure",
  "deferred",
  "suppressed",
]);

function noticeRows(diagnostics: ReferralDiagnostics) {
  return diagnostics.engagement.filter(
    (row) => row.purpose === "referral_reward" && NOTICE_FAILED.has(row.state),
  );
}

export function ReferralDiagnosticsCard() {
  const access = useCanAccess({ resource: "configuration", action: "edit" });
  const diagnostics = useQuery({
    queryKey: ["referral-diagnostics"],
    queryFn: () => referralRequest<ReferralDiagnostics>("diagnostics?days=14"),
    enabled: access.canAccess === true,
    retry: false,
  });
  const [preview, setPreview] = useState<CampaignPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!access.canAccess) return null;
  async function campaign(launch: boolean) {
    setBusy(true);
    setError(null);
    try {
      const result = await referralRequest<CampaignPreview>(
        launch && preview
          ? `campaigns/${preview.preview_id}/launch`
          : "campaigns/preview",
        {
          method: "POST",
          body: JSON.stringify(
            launch ? { audience_digest: preview?.audience_digest } : {},
          ),
        },
      );
      setPreview(result);
      void diagnostics.refetch();
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : "Campaign request failed",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card>
      <CardContent className="py-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">
            Referral delivery health · last 14 days
          </h2>
          <Button
            variant="outline"
            onClick={() => void diagnostics.refetch()}
            disabled={diagnostics.isFetching}
          >
            Refresh health
          </Button>
        </div>
        {diagnostics.isPending ? (
          <p>Loading health…</p>
        ) : diagnostics.isError ? (
          <p role="alert">
            Referral health is unavailable. Retry after checking the bot
            connection.
          </p>
        ) : (
          diagnostics.data && (
            <>
              <p>
                {diagnostics.data.environment === "stg"
                  ? "Production (stg)"
                  : "Engineering staging (staging)"}{" "}
                · {new Date(diagnostics.data.as_of).toLocaleString()}
              </p>
              <p>
                {diagnostics.data.feedback.opportunities} feedback opportunities
                · {diagnostics.data.feedback.rated} rated ·{" "}
                {diagnostics.data.feedback.unrated} unrated
              </p>
              <p>
                {diagnostics.data.rewards.states.granted ?? 0} rewards granted ·{" "}
                {diagnostics.data.rewards.notified} notification submissions
                recorded
              </p>
              {(diagnostics.data.rewards.granted_without_notice ?? 0) > 0 && (
                <p role="alert" className="text-destructive">
                  {diagnostics.data.rewards.granted_without_notice} rewards
                  granted with no notice sent — these referrers have credits and
                  have not been told.
                </p>
              )}
              {noticeRows(diagnostics.data).length > 0 && (
                <div className="space-y-1">
                  <h3 className="font-medium text-sm">
                    Reward notices pending or failed
                  </h3>
                  <ul className="text-sm">
                    {noticeRows(diagnostics.data).map((row) => (
                      <li key={`${row.sender_key}:${row.state}:${row.reason}`}>
                        {row.count} on {row.sender_key ?? "unknown sender"} ·{" "}
                        {row.state === "unknown"
                          ? "outcome unknown, contact blocked until resolved"
                          : row.state}{" "}
                        · {row.reason ?? "no reason recorded"}
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-muted-foreground">
                    A template belongs to one WhatsApp business account. A
                    failure on one Felix number says nothing about the others.
                  </p>
                </div>
              )}
              <p className="text-sm text-muted-foreground">
                Counts use correlated Echo feedback. Older feedback is excluded.
                Submission does not confirm delivery or read.
              </p>
              {diagnostics.data.engagement.length > 0 && (
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th>Purpose</th>
                      <th>Sender</th>
                      <th>State</th>
                      <th>Delivery</th>
                      <th>Reason</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diagnostics.data.engagement.map((row) => (
                      <tr
                        key={`${row.purpose}:${row.sender_key}:${row.state}:${row.provider_status}:${row.reason}`}
                      >
                        <td>{row.purpose}</td>
                        <td>{row.sender_key ?? "—"}</td>
                        <td>{row.state}</td>
                        <td>{row.provider_status ?? "—"}</td>
                        <td>{row.reason ?? "—"}</td>
                        <td>{row.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )
        )}
        <h3 className="font-medium">Remind contacts with no credits</h3>
        <p className="text-sm">
          Preview eligible B2C contacts before sending. Consent, current
          credits, conversation activity and template approval are checked again
          when each message is dispatched.
        </p>
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => void campaign(false)}
        >
          Preview audience
        </Button>
        {preview && (
          <div className="space-y-2">
            <p>
              {preview.eligible} eligible · preview expires{" "}
              {new Date(preview.expires_at).toLocaleString()}
            </p>
            <p className="text-sm">
              Excluded:{" "}
              {Object.entries(preview.excluded)
                .map(([reason, count]) => `${reason}: ${count}`)
                .join(", ") || "none"}
            </p>
            {preview.launch_blocker && (
              <p role="status">Sending unavailable: {preview.launch_blocker}</p>
            )}
            {preview.status === "launched" ? (
              <p role="status">
                Campaign queued. Check delivery health for dispatch outcomes.
              </p>
            ) : (
              <Button
                disabled={
                  busy ||
                  !!preview.launch_blocker ||
                  preview.eligible === 0 ||
                  Date.parse(preview.expires_at) <= Date.now()
                }
                onClick={() => void campaign(true)}
              >
                Send referral reminder to {preview.eligible} contacts
              </Button>
            )}
          </div>
        )}
        {error && (
          <p role="alert" className="text-destructive">
            {error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
